import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import { OrderCard } from "@/components/admin/OrderCard";

const FILTERS = ["all", "new", "quoted", "confirmed", "completed", "cancelled"];

const Login = ({ onIn }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onIn(data);
    } catch (x) {
      setErr(errorText(x));
    }
  };
  const inp = "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[#F472B6]";
  return (
    <div className="grid min-h-screen place-items-center bg-[#1A1412] px-5">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-3xl border border-white/10 bg-[#261E1B] p-8" data-testid="admin-login-form">
        <LogoMark className="h-12 w-12" />
        <h1 className="f-display text-4xl font-semibold text-white">Order board</h1>
        <p className="text-sm text-[#D6C7C0]">Sign in to see incoming orders.</p>
        <input className={inp} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" />
        <input className={inp} placeholder="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" />
        {err && <p className="text-sm text-[#FDA4AF]" data-testid="admin-login-error">{err}</p>}
        <button className="w-full rounded-full bg-[#E11D48] py-3.5 text-sm font-semibold text-white" data-testid="admin-login-submit-button">Sign in</button>
        <Link to="/" className="block text-center text-xs text-[#D6C7C0] hover:text-white" data-testid="admin-back-home">← Back to shop</Link>
      </form>
    </div>
  );
};

const Board = ({ onOut }) => {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/orders");
      setOrders(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(errorText(e));
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => null);
    onOut();
  };
  const shown = filter === "all" ? orders : orders.filter((o) => o.status === filter);
  const onChange = (o) => setOrders((p) => p.map((x) => (x.id === o.id ? o : x)));
  const onDelete = (id) => setOrders((p) => p.filter((x) => x.id !== id));

  return (
    <div className="min-h-screen bg-[#1A1412] text-[#F5EBE6]" data-testid="admin-board">
      <header className="flex items-center justify-between border-b border-white/10 px-5 py-5 sm:px-8">
        <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link">
          <LogoMark className="h-9 w-9" />
          <span className="f-display text-2xl italic">Order board</span>
        </Link>
        <div className="flex items-center gap-2">
          <button onClick={load} className="rounded-full border border-white/15 p-2.5 hover:bg-white/10" data-testid="admin-refresh-button" aria-label="Refresh"><RefreshCw className="h-4 w-4" /></button>
          <button onClick={logout} className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:bg-white/10" data-testid="admin-logout-button"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      </header>
      <div className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <div className="no-scrollbar mb-8 flex gap-2 overflow-x-auto">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`admin-filter-${f}`}
              className={`shrink-0 rounded-full px-4 py-2 text-sm capitalize ${filter === f ? "bg-[#F472B6] text-[#1A1412]" : "border border-white/15 text-[#D6C7C0]"}`}
            >
              {f} ({f === "all" ? orders.length : orders.filter((o) => o.status === f).length})
            </button>
          ))}
        </div>
        {shown.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-white/15 p-12 text-center text-[#D6C7C0]" data-testid="admin-empty">No orders here yet.</p>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {shown.map((o) => <OrderCard key={o.id} order={o} onChange={onChange} onDelete={onDelete} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  if (user === null) return <div className="min-h-screen bg-[#1A1412]" />;
  return user ? <Board onOut={() => setUser(false)} /> : <Login onIn={setUser} />;
}
